import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewEncapsulation,
  SimpleChanges
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PhxSelectOption, PhxSelectOptionContext } from './select.types';
import mixinDisabled from '../../mixins/disabled';
import { ChevronRight } from '../icon/icon-map';
import { escape } from '../../utils';
import { PhxSelectDefaultConfig } from './phx-select.common';

const baseOptionMixin = mixinDisabled(
  class {
    constructor() { }
  }
);

@Component({
  selector: 'phx-select-option-item',
  templateUrl: './option-item.component.html',
  styleUrls: ['./option-item.component.scss'],
  inputs: ['disabled'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'phx-select-option-item',
    '[class.phx-select-option]': '!groupLabel',
    '[class.phx-select-group-label]': 'groupLabel',
    '[class.phx-select-option-selected]': '!multiple && selected',
    '[class.phx-select-option-selected-multiple]': 'multiple && selected',
    '[class.phx-select-option-disabled]': 'disabled',
    '[class.phx-select-option-focused]': 'isFocused && !groupLabel && !disabled',
    '[style.height.px]': 'height',
    '[class.phx-select-option-has-prefix]': 'prefixIcon',
    '[class.phx-select-option-link]': 'isLink',
    '[class.phx-select-option-parent-selection-disabled]': 'isParentSelectionDisabled',
    '(click)': 'changeSelection()'
  },
})
export class PhxOptionItemComponent extends baseOptionMixin implements OnChanges, OnInit, OnDestroy {
  @Input() public label: string;
  @Input() public value: string | any | null;
  @Input() public groupLabel: string;
  @Input() public data: any;
  @Input() public children: PhxSelectOption[];
  @Input() public selected: boolean;
  @Input() public multiple: boolean;
  @Input() public isDivider: boolean;
  @Input() public isLink: boolean;
  @Input() public showDivider: boolean;
  @Input() public subText: string;
  @Input() public isNestedOptions = false;
  @Input() public showSubText: boolean = false;
  @Input() public truncateSubtext: boolean = true;
  @Input() public showRadio: boolean = false;
  @Input() public isIndeterminate: boolean = false;
  @Input() public searched: boolean = false;
  @Input() public ancestors: PhxSelectOption[];
  @Input() public optionHeight: number;
  @Input() public isTreeView: boolean = false;
  @Input() public isExpanded: boolean = false;
  @Input() public prefixIcon: string;
  @Input() public highlightSubtext: boolean = false;
  @Input() public optionTemplate: TemplateRef<{ $implicit: PhxSelectOptionContext }>;
  @Input() public searchedText: string;
  @Input() public isLastOption: boolean = false;
  @Input() public isMobileView: boolean = false;
  @Input() public isVirtualScrollOption: boolean = true;
  @Input() public isCustomScrollEnabled: boolean = false;
  @Input() public shouldHighlightMatchedSubtext?: boolean = true;
  @Input() public isFocused: boolean = false;
  @Input() public tooltip: string | TemplateRef<any>;
  @Input() public isCustomTooltipEnabled: boolean = false;
  @Input() public isTooltipOnTruncateEnabled: boolean = false;
  @Input() public disableParentSelection: boolean = false; // Only applicable for single-select nested options
  @Input() public suffixTemplate: TemplateRef<any> | null = null;

  @Output() onSelect: EventEmitter<PhxSelectOption> = new EventEmitter<PhxSelectOption>();
  @Output() onShowChildren: EventEmitter<PhxSelectOption> = new EventEmitter<PhxSelectOption>();
  @Output() onExpandCollapse: EventEmitter<PhxSelectOption> = new EventEmitter<PhxSelectOption>();
  @Output() onLinkClick: EventEmitter<PhxSelectOption> = new EventEmitter<PhxSelectOption>();
  @Output() onLastOptionVisible: EventEmitter<boolean> = new EventEmitter<boolean>();

  public readonly chevronRightIcon: string = ChevronRight;
  public readonly dividerHeight: number = PhxSelectDefaultConfig.dividerHeight;
  public highlightedLabel: SafeHtml;
  public highlightedSubText: SafeHtml;

  private _intersectionObserver: IntersectionObserver | null = null;
  private _hasEmittedVisibility: boolean = false;

  constructor(
    private _sanitizer: DomSanitizer,
    public elementRef: ElementRef<HTMLElement>
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { label, subText, searchedText } = changes;

    if (this.label && (searchedText || label)) {
      this.highlightedLabel = this.getHighlightedText(this.label, 'primary');
    }

    if (this.subText && (searchedText || subText)) {
      if (this.shouldHighlightMatchedSubtext) {
        this.highlightedSubText = this.getHighlightedText(this.subText, 'secondary');
      }
      else {
        this.highlightedSubText = this.subText;  // If shouldHighlightMatchedSubtext is false assign the original subtext as the highlightedSubText
      }
    }

    if (changes['isLastOption'] || changes['isCustomScrollEnabled'] || changes['isVirtualScrollOption'] || changes['groupLabel'] || changes['value']) {
      this.syncIntersectionObserver();
    }
  }

  public ngOnInit(): void {
    this.syncIntersectionObserver();
  }

  public ngOnDestroy(): void {
    this.cleanupIntersectionObserver();
  }

  private syncIntersectionObserver(): void {
    if (!this.shouldObserveLastOption()) {
      this.cleanupIntersectionObserver();
      this._hasEmittedVisibility = false;
      return;
    }

    if (!this._intersectionObserver) {
      this._hasEmittedVisibility = false;
      this.setupIntersectionObserver();
    }
  }

  private shouldObserveLastOption(): boolean {
    return (this.isCustomScrollEnabled || !this.isVirtualScrollOption) && this.isLastOption && !this.groupLabel;
  }

  private setupIntersectionObserver(): void {
    if (typeof IntersectionObserver !== 'undefined' && !this._intersectionObserver) {
      this._intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && entry.isIntersecting && !this._hasEmittedVisibility) {
            this._hasEmittedVisibility = true;
            this.onLastOptionVisible.emit(true);
            this.cleanupIntersectionObserver();
          }
        },
        { threshold: 0.1 }
      );

      this._intersectionObserver.observe(this.elementRef.nativeElement);
    }
  }

  private cleanupIntersectionObserver(): void {
    this._intersectionObserver?.disconnect();
    this._intersectionObserver = null;
  }

  public get optionData(): PhxSelectOption {
    return {
      label: this.label,
      value: this.value,
      groupLabel: this.groupLabel,
      subText: this.subText,
      disabled: this.disabled,
      data: this.data,
      children: this.children,
    };
  }

  public get templateContext(): PhxSelectOptionContext {
    return {
      ...this.optionData,
      selected: this.selected,
      multiple: this.multiple,
      ancestors: this.ancestors,
    };
  }

  public get ancestorsPath(): string {
    if (!this.ancestors?.length) return '';
    return this.ancestors.map(a => a.label).join(' > ');
  }

  public get isParentSelectionDisabled(): boolean {
    return this.disableParentSelection && !!this.children?.length && !this.multiple;
  }

  /**
   * Returns the appropriate tooltip content for the label area.
   * Only returns content for searched nested options with no ancestors or with exactly 1 ancestor.
   * For all other cases, returns null so child elements can use their own tooltips.
   */
  public get labelTooltipContent(): string | null {
    // Apply parent tooltip for searched nested options
    if (this.searched && this.isNestedOptions) {
      // For top-level items with no ancestors, return just the label
      if (!this.ancestors?.length) {
        return this.label;
      }
      // For items with exactly 1 ancestor, return combined path
      if (this.ancestors?.length === 1) {
        return `${this.ancestors[0]?.label} > ${this.label}`;
      }
    }
    return null;
  }

  public changeSelection() {
    if (this.groupLabel || this.disabled || this.isDivider) return;
    if (this.isLink) {
      this.onLinkClick.emit(this.optionData);
      return;
    }
    if (this.isParentSelectionDisabled) {
      const mouseClickEvent: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.showChildren(mouseClickEvent);
      return;
    }
    this.onSelect.emit(this.optionData);
  }

  public showChildren(event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.onShowChildren.emit(this.optionData);
  }

  public expandCollapseOption(event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.onExpandCollapse.emit(this.optionData);
  }

  public get height(): number {
    // Do not apply fixed height if subtext is not truncated or virtual scroll is not used
    if (!this.truncateSubtext || !this.isVirtualScrollOption) {
      return null;
    }

    if (this.isDivider) {
      return PhxSelectDefaultConfig.dividerHeight;
    }

    // If we are not using custom scroll strategy then height would be the optionHeight
    if (!this.isCustomScrollEnabled) {
      // When showing the divider in case of groupLabel
      if (this.groupLabel && this.showDivider) {
        return PhxSelectDefaultConfig.groupLabelHeightWithFlagOff + this.optionHeight;
      }
      if (this.groupLabel) {
        return PhxSelectDefaultConfig.groupLabelHeightWithFlagOff;
      }
      return this.optionHeight;
    }

    // When optionHeight is explicitly provided and custom scroll is enabled, use it directly
    // This ensures consistent height for options with custom templates (phxOptionTemplate)
    if (this.optionHeight) {
      // Group labels should not have fixed height because dividers appear conditionally:
      // - The first group label has no divider above it
      // - Subsequent group labels have dividers, requiring additional height
      if (this.groupLabel) return null;
      return this.optionHeight;
    }

    let height: number;

    if (this.groupLabel) return null;
    else if ((this.subText && this.showSubText) || (this.isNestedOptions && this.ancestors.length > 1 && this.searched)) height = PhxSelectDefaultConfig.optionHeightWithSubText;
    else height = PhxSelectDefaultConfig.optionHeight;

    return this.isMobileView ? (height += PhxSelectDefaultConfig.mobileViewPadding) : height;
  }

  public get groupDividerHeight(): number {
    if (this.truncateSubtext || this.isVirtualScrollOption) {
      return this.isCustomScrollEnabled ? this.dividerHeight : this.optionHeight;
    }
    return null;

  }

  public getHighlightedText(text: string, type: 'primary' | 'secondary' = 'primary'): SafeHtml {
    if (!this.searchedText || this.isLink) return escape(text);

    const escapedText = escape(text);
    const escapedSearchTextForHTML = escape(this.searchedText);
    const regexHTML = new RegExp(escapedSearchTextForHTML, 'gi');
    const cssClasses = type === 'secondary' ? 'phx-web-type-small-emphasis phx-select-option-label-subtext-highlighted' : 'phx-web-type-default-emphasis';
    const sanitizedText = escapedText.replace(regexHTML, (match) =>
      `<span class="${cssClasses}">${match}</span>`
    );

    return this._sanitizer.bypassSecurityTrustHtml(sanitizedText);
  }

  // TODO: Remove this function when cleaning the dynamic-config `virtual-scroll-updates`
  public get optionItemHeight(): number | null {
    if (!this.truncateSubtext || !this.isVirtualScrollOption) {   // Do not apply fixed height if subtext is not truncated or virtual scroll is not used
      return null;
    }
    return this.groupLabel && this.showDivider ? this.optionHeight * 2 : this.optionHeight;
  }
}
